<template>
  <div>
    <v-autocomplete
      :required="required"
      :prepend-icon="!hideIcon ? 'mdi-domain' : ''"
      density="compact"
      @update:model-value="updateValue"
      :model-value="modelValue"
      @keydown.enter.prevent="() => {}"
      @update:search="onSearchInput"
      :items="companies"
      data-testid="select-company-search-field"
      ref="companySelect"
      item-value="id"
      return-object
      no-filter
      :clearable="clearable"
      :hide-no-data="!filters.searchStr"
      :variant="variant"
      persistent-hint
      :hide-details="!required"
      :label="placeholder"
      :loading="isLoading"
      :disabled="disabled">
      <template v-slot:selection="{ item }">
        <div class="company-select-selection">
          <span v-if="!hideScac" class="mr-2">[{{ item.raw.scac }}]</span>
          <span class="company-select-selection__name">
            {{ truncateString(item.raw.name, maxSelectionChars) }}
          </span>
        </div>
      </template>

      <template v-slot:item="{ item, props }">
        <v-list-item v-bind="props" :title="null" role="option">
          <v-list-item-title v-html="`${item.raw.name}`"></v-list-item-title>
          <v-list-item-subtitle v-if="!hideScac">SCAC: {{ item.raw.scac }}</v-list-item-subtitle>
        </v-list-item>

        <v-divider
          v-if="companies.indexOf(item.raw) < companies.length - 1"
          :key="companies.indexOf(item.raw)"></v-divider>
      </template>

      <template v-slot:append-item>
        <v-divider></v-divider>
        <v-list-item>
          <v-list-item-title>
            <v-btn color="primary" block @click="openCompanyDialog">
              Create a new Carrier Company
            </v-btn>
          </v-list-item-title>
        </v-list-item>
      </template>

      <template #label v-if="required">
        {{ placeholder }}
        <span class="text-red"><strong>*</strong></span>
      </template>
    </v-autocomplete>
  </div>
</template>

<script>
import { truncateString } from '@nova/core';
import selectMixin from '@satellite/components/mixins/selectMixin';
import dataListMixin from '@satellite/components/mixins/dataListMixin';
import { useDialogTrigger } from '@/composables';

/**
 * Company Select
 * @displayName Company Select
 */
export default {
  setup() {
    return { truncateString };
  },
  mixins: [selectMixin, dataListMixin],
  props: {
    /**
     * @model
     */
    modelValue: {
      type: Object,
      required: false
    },
    /**
     * Is input disabled
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Input placeholder
     */
    placeholder: {
      type: String,
      required: false,
      default: 'Start Typing a company name, scac, usdot, or mc'
    },
    /*
     * Mixpanel entry point
     */
    entryPoint: {
      type: String,
      required: true
    },
    /**
     * Input variant
     */
    variant: {
      type: String,
      required: false,
      default: 'default'
    },
    /**
     * Hide SCAC code in the select options and selection
     */
    hideScac: {
      type: Boolean,
      required: false,
      default: false
    },
    /*
     * Show the clear input icon
     */
    clearable: {
      type: Boolean,
      required: false,
      default: false
    },
    /**
     * Max selection chars for truncate
     */
    maxSelectionChars: {
      type: Number,
      required: false,
      default: 150
    }
  },
  data() {
    return {
      companies: [],
      filters: {
        searchStr: null
      },
      searchFields: ['name', 'scac', 'mc', 'usdot'],
      isLoading: false
    };
  },
  methods: {
    /**
     * V-model
     * @public
     * @param companyId
     */
    updateValue(company) {
      /**
       * Emits input with company Id
       * @event input
       * @property {string} companyId
       */
      this.$emit('update:model-value', company);
    },
    /**
     * Retrieves data when filters are updated
     * @public
     */
    async getData() {
      this.isLoading = true;

      if (!this.filters.searchStr) {
        this.isLoading = false;
        return (this.companies = []);
      }

      const { items } = await this.services.company.autocomplete({
        limit: 15,
        search: this.filters.searchStr ?? ''
      });
      this.companies = items;
      this.isLoading = false;
    },
    openCompanyDialog() {
      const { triggerDialog } = useDialogTrigger('open-create-carrier-company-dialog');
      triggerDialog({
        mixpanelEntryPoint: this.entryPoint,
        onCompanyCreated: this.handleCompanyCreated
      });
    },
    /**
     * Handles new company creation
     * @public
     * @param {Company} company
     */
    handleCompanyCreated(company) {
      this.$emit('companyChanged', company);
      this.companies.push(company);
      this.updateValue(company);
      this.$refs.companySelect.isMenuActive = false;
    },
    getSelectedItem() {
      return this.$refs.companySelect.selectedItem;
    },
    emitCompanyChanged(value) {
      const newCompany = this.companies.find(company => company.id === value);
      this.$emit('companyChanged', newCompany);
    },
    onSearchInput(inputVal) {
      this.filters.searchStr = inputVal;
    }
  },
  async mounted() {
    // If there is already a value set we need to fetch the company record and add it to our companies list
    // By default this component does not have any companies until they are searched for
    if (this.value && !this.companies.length) {
      await axios.get(`/company/${this.modelValue}`).then(response => {
        this.companies.push(response.data.data);
      });
    }
  }
};
</script>
<style scoped>
.company-select-selection {
  display: flex;
  align-items: center;
  max-width: 100%;
  overflow: hidden;
}

.company-select-selection__name {
  display: inline-block;
  max-width: 180px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
}
</style>
